import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth, useTheme, API } from "@/App";
import {
  User, Mail, Moon, Sun, Shield, Bell, Trash2, Camera, Loader2, Clock, Check, KeyRound,
  Eye, EyeOff, AlertTriangle, XCircle, Hourglass,
} from "lucide-react";
import { toast } from "sonner";
import { Turnstile } from "@marsidev/react-turnstile";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const AVATAR_MAX_SIZE = 3 * 1024 * 1024; // 3MB, harus sinkron dengan backend

const nextAllowedLabelFrom = (seconds) =>
  new Date(Date.now() + seconds * 1000).toLocaleString("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  });

const formatCooldown = (seconds) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return hours > 0 ? `${days} hari ${hours} jam` : `${days} hari`;
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours} jam ${minutes} menit` : `${minutes} menit`;
};

const SettingsPage = () => {
  const { user, setUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [notifications, setNotifications] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", next: "", confirm: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [deletionRequest, setDeletionRequest] = useState(null);
  const [loadingDeletionRequest, setLoadingDeletionRequest] = useState(true);
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);
  const [deletionReason, setDeletionReason] = useState("");
  const [deletionCaptchaToken, setDeletionCaptchaToken] = useState(null);
  const [submittingDeletion, setSubmittingDeletion] = useState(false);
  const [cancellingDeletion, setCancellingDeletion] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
  }, [user?.name]);

  const loadDeletionRequest = async () => {
    setLoadingDeletionRequest(true);
    try {
      const res = await axios.get(`${API}/account/deletion-request`, { withCredentials: true });
      setDeletionRequest(res.data.request);
    } catch {
      // diamkan — Danger Zone cukup fallback ke tombol default
    } finally {
      setLoadingDeletionRequest(false);
    }
  };

  useEffect(() => {
    loadDeletionRequest();
  }, []);

  const openDeletionDialog = () => {
    setDeletionReason("");
    setDeletionCaptchaToken(null);
    setDeletionDialogOpen(true);
  };

  const submitDeletionRequest = async () => {
    if (TURNSTILE_SITE_KEY && !deletionCaptchaToken) {
      toast.error("Mohon verifikasi captcha");
      return;
    }
    setSubmittingDeletion(true);
    try {
      const res = await axios.post(
        `${API}/account/deletion-request`,
        { reason: deletionReason.trim() || null, captcha_token: deletionCaptchaToken },
        { withCredentials: true }
      );
      setDeletionRequest(res.data.request);
      setDeletionDialogOpen(false);
      toast.success("Permintaan penghapusan akun terkirim, menunggu peninjauan tim Campus Link.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal mengajukan penghapusan akun");
    } finally {
      setSubmittingDeletion(false);
    }
  };

  const cancelDeletionRequest = async () => {
    setCancellingDeletion(true);
    try {
      await axios.post(`${API}/account/deletion-request/cancel`, {}, { withCredentials: true });
      toast.success("Permintaan penghapusan akun dibatalkan");
      await loadDeletionRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal membatalkan permintaan");
    } finally {
      setCancellingDeletion(false);
    }
  };

  const nameCooldownSeconds = user?.name_cooldown_seconds ?? null;
  const isNameOnCooldown = savingName ? false : nameCooldownSeconds != null && nameCooldownSeconds > 0;
  const isNameDirty = name.trim() !== (user?.name || "").trim();
  const nameNextAllowedLabel = useMemo(
    () => (nameCooldownSeconds ? nextAllowedLabelFrom(nameCooldownSeconds) : null),
    [nameCooldownSeconds]
  );

  const passwordCooldownSeconds = user?.password_cooldown_seconds ?? null;
  const isPasswordOnCooldown =
    savingPassword ? false : passwordCooldownSeconds != null && passwordCooldownSeconds > 0;
  const passwordNextAllowedLabel = useMemo(
    () => (passwordCooldownSeconds ? nextAllowedLabelFrom(passwordCooldownSeconds) : null),
    [passwordCooldownSeconds]
  );

  const saveName = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 60) {
      toast.error("Nama harus 2 sampai 60 karakter");
      return;
    }
    setSavingName(true);
    try {
      const response = await axios.patch(
        `${API}/auth/me`,
        { name: trimmed },
        { withCredentials: true }
      );
      setUser(response.data);
      toast.success("Nama berhasil diperbarui");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memperbarui nama");
    } finally {
      setSavingName(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    if (passwordForm.next !== passwordForm.confirm) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    if (passwordForm.next.length < 8) {
      toast.error("Password baru minimal 8 karakter");
      return;
    }
    setSavingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: passwordForm.current, new_password: passwordForm.next },
        { withCredentials: true }
      );
      setPasswordForm({ current: "", next: "", confirm: "" });
      toast.success("Password berhasil diganti. Sesi di perangkat lain telah dikeluarkan.");
      const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(me.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal mengganti password");
    } finally {
      setSavingPassword(false);
    }
  };

  const cooldownSeconds = user?.avatar_cooldown_seconds ?? null;
  const isOnCooldown = uploadingAvatar ? false : cooldownSeconds != null && cooldownSeconds > 0;
  const nextAllowedLabel = useMemo(() => {
    if (!cooldownSeconds) return null;
    const nextDate = new Date(Date.now() + cooldownSeconds * 1000);
    return nextDate.toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
  }, [cooldownSeconds]);

  const uploadAvatarFile = async (file) => {
    if (isOnCooldown) {
      toast.error(`Foto profil hanya bisa diganti setiap 7 hari. Coba lagi setelah ${nextAllowedLabel}.`);
      return;
    }
    if (file.size > AVATAR_MAX_SIZE) {
      toast.error("Ukuran file maksimal 3MB");
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await axios.post(`${API}/upload/avatar`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const profileRes = await axios.patch(
        `${API}/auth/me`,
        { picture: uploadRes.data.url },
        { withCredentials: true }
      );
      setUser(profileRes.data);
      toast.success("Foto profil berhasil diperbarui");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal mengunggah foto profil");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) uploadAvatarFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    if (isOnCooldown || uploadingAvatar) return;
    const file = event.dataTransfer.files?.[0];
    if (file) uploadAvatarFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!isOnCooldown && !uploadingAvatar) setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Kelola akun dan preferensi Anda
          </p>
        </div>

        {/* Profile */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile
            </CardTitle>
            <CardDescription>Informasi akun Anda</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-4">
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`relative rounded-full transition-shadow ${
                  isDragOver ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""
                }`}
              >
                <Avatar className="w-16 h-16">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-primary text-2xl font-bold text-primary-foreground">
                    {user?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar || isOnCooldown}
                  className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center border-2 border-background disabled:opacity-60"
                  aria-label="Ubah foto profil"
                  title={isOnCooldown ? `Bisa diganti lagi setelah ${nextAllowedLabel}` : "Ubah foto profil"}
                >
                  {uploadingAvatar ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : isOnCooldown ? (
                    <Clock className="w-3.5 h-3.5" />
                  ) : (
                    <Camera className="w-3.5 h-3.5" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.heic,.heif"
                  className="hidden"
                  onChange={handleAvatarChange}
                  disabled={uploadingAvatar || isOnCooldown}
                  data-testid="avatar-upload-input"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-lg">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                {isOnCooldown ? (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Bisa diganti lagi {formatCooldown(cooldownSeconds)} lagi ({nextAllowedLabel})
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="text-xs text-primary hover:underline mt-1"
                  >
                    Ganti foto profil
                  </button>
                )}
                <p className="text-[11px] text-muted-foreground mt-1">
                  Seret &amp; lepas gambar ke avatar, atau klik ikon kamera. Maks 3MB. Hanya bisa diganti
                  sekali setiap 7 hari.
                </p>
              </div>
            </div>
            <Separator />
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="profile-name">Nama</Label>
                <Input
                  id="profile-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={savingName || isNameOnCooldown}
                  maxLength={60}
                  data-testid="profile-name-input"
                />
                {isNameOnCooldown ? (
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Bisa diganti lagi {formatCooldown(nameCooldownSeconds)} lagi ({nameNextAllowedLabel})
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    2–60 karakter. Hanya bisa diganti sekali setiap 7 hari.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user?.email || ""} disabled />
                <p className="text-[11px] text-muted-foreground">
                  Email adalah identitas login — hubungi administrator bila perlu diubah.
                </p>
              </div>
            </div>
            {isNameDirty && !isNameOnCooldown && (
              <div className="flex items-center gap-2">
                <Button onClick={saveName} disabled={savingName} data-testid="save-name-btn">
                  {savingName ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  {savingName ? "Menyimpan..." : "Simpan Nama"}
                </Button>
                <Button variant="ghost" onClick={() => setName(user?.name || "")} disabled={savingName}>
                  Batal
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {theme === "dark" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              Appearance
            </CardTitle>
            <CardDescription>Kustomisasi tampilan aplikasi</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Dark Mode</p>
                <p className="text-sm text-muted-foreground">
                  {theme === "dark" ? "Mode gelap aktif" : "Mode terang aktif"}
                </p>
              </div>
              <Switch
                checked={theme === "dark"}
                onCheckedChange={toggleTheme}
                data-testid="theme-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notifications
            </CardTitle>
            <CardDescription>Pengaturan notifikasi</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-muted-foreground">
                  Terima update via email
                </p>
              </div>
              <Switch
                checked={notifications}
                onCheckedChange={setNotifications}
                data-testid="notifications-switch"
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Weekly Report</p>
                <p className="text-sm text-muted-foreground">
                  Laporan mingguan performa link
                </p>
              </div>
              <Switch defaultChecked data-testid="weekly-report-switch" />
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Security
            </CardTitle>
            <CardDescription>Keamanan akun</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="w-4 h-4" />
                <p className="font-medium">Ganti Password</p>
              </div>
              {!user?.has_password ? (
                <p className="text-sm text-muted-foreground">
                  Akun ini masuk lewat Google, jadi tidak memiliki password. Hubungi administrator
                  bila ingin menyetel password.
                </p>
              ) : isPasswordOnCooldown ? (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  Bisa diganti lagi {formatCooldown(passwordCooldownSeconds)} lagi ({passwordNextAllowedLabel})
                </p>
              ) : (
                <form onSubmit={savePassword} className="space-y-3 mt-2">
                  <p className="text-xs text-muted-foreground">
                    Minimal 8 karakter. Hanya bisa diganti sekali setiap 7 hari, dan sesi di perangkat
                    lain akan dikeluarkan.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="current-password" className="text-xs">Password Lama</Label>
                      <Input
                        id="current-password"
                        type={showPassword ? "text" : "password"}
                        value={passwordForm.current}
                        onChange={(e) => setPasswordForm((c) => ({ ...c, current: e.target.value }))}
                        autoComplete="current-password"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="new-password" className="text-xs">Password Baru</Label>
                      <Input
                        id="new-password"
                        type={showPassword ? "text" : "password"}
                        value={passwordForm.next}
                        onChange={(e) => setPasswordForm((c) => ({ ...c, next: e.target.value }))}
                        autoComplete="new-password"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="confirm-password" className="text-xs">Ulangi Password Baru</Label>
                      <Input
                        id="confirm-password"
                        type={showPassword ? "text" : "password"}
                        value={passwordForm.confirm}
                        onChange={(e) => setPasswordForm((c) => ({ ...c, confirm: e.target.value }))}
                        autoComplete="new-password"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="submit" disabled={savingPassword} data-testid="save-password-btn">
                      {savingPassword ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <KeyRound className="w-4 h-4 mr-2" />
                      )}
                      {savingPassword ? "Menyimpan..." : "Ganti Password"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                      {showPassword ? "Sembunyikan" : "Tampilkan"}
                    </Button>
                  </div>
                </form>
              )}
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Two-Factor Authentication</p>
                <p className="text-sm text-muted-foreground">
                  Tambahan keamanan untuk akun Anda
                </p>
              </div>
              <Button variant="outline" size="sm" disabled>
                Coming Soon
              </Button>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Active Sessions</p>
                <p className="text-sm text-muted-foreground">
                  Kelola sesi login aktif
                </p>
              </div>
              <Button variant="outline" size="sm" disabled>
                Coming Soon
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="dashboard-card border-destructive/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>Aksi berbahaya dan tidak dapat dibatalkan</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingDeletionRequest ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Memeriksa status akun...
              </div>
            ) : deletionRequest?.status === "pending" ? (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
                <div className="flex items-start gap-3">
                  <Hourglass className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-amber-600 dark:text-amber-400">
                      Menunggu proses verifikasi oleh tim Campus Link
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Diajukan pada {new Date(deletionRequest.requested_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}.
                      Akun Anda masih bisa dipakai normal sampai permintaan ini ditinjau.
                    </p>
                    {deletionRequest.reason && (
                      <p className="text-sm text-muted-foreground mt-2 italic">
                        Alasan: &ldquo;{deletionRequest.reason}&rdquo;
                      </p>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={cancelDeletionRequest}
                      disabled={cancellingDeletion}
                      data-testid="cancel-deletion-btn"
                    >
                      {cancellingDeletion ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Batalkan Permintaan
                    </Button>
                  </div>
                </div>
              </div>
            ) : deletionRequest?.status === "rejected" ? (
              <div className="rounded-lg border border-border p-4">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">Permintaan sebelumnya ditolak</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Ditinjau pada {new Date(deletionRequest.reviewed_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}.
                    </p>
                    {deletionRequest.reject_reason && (
                      <p className="text-sm text-muted-foreground mt-2 italic">
                        Catatan tim: &ldquo;{deletionRequest.reject_reason}&rdquo;
                      </p>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      className="mt-3"
                      onClick={openDeletionDialog}
                      data-testid="delete-account-btn"
                    >
                      Ajukan Ulang
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Delete Account</p>
                  <p className="text-sm text-muted-foreground">
                    Hapus akun dan semua data secara permanen
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={openDeletionDialog}
                  data-testid="delete-account-btn"
                >
                  Delete Account
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog ajukan penghapusan akun */}
      <Dialog open={deletionDialogOpen} onOpenChange={setDeletionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Ajukan Penghapusan Akun
            </DialogTitle>
            <DialogDescription>
              Permintaan ini akan ditinjau tim Campus Link sebelum akun dan seluruh data
              (link, microsite, statistik klik) dihapus permanen. Anda bisa membatalkan
              selama permintaan masih menunggu.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="deletion-reason">Alasan penghapusan akun (opsional)</Label>
              <Textarea
                id="deletion-reason"
                placeholder="Ceritakan alasan Anda ingin menghapus akun..."
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
                maxLength={1000}
                rows={4}
                data-testid="deletion-reason-input"
              />
            </div>
            {TURNSTILE_SITE_KEY && (
              <div className="flex justify-center">
                <Turnstile
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setDeletionCaptchaToken(token)}
                  onExpire={() => setDeletionCaptchaToken(null)}
                  onError={() => setDeletionCaptchaToken(null)}
                  options={{ theme: theme === "dark" ? "dark" : "light" }}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletionDialogOpen(false)}>
              Batal
            </Button>
            <Button
              variant="destructive"
              onClick={submitDeletionRequest}
              disabled={submittingDeletion || (TURNSTILE_SITE_KEY && !deletionCaptchaToken)}
              data-testid="confirm-delete-account-btn"
            >
              {submittingDeletion ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Ajukan Penghapusan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default SettingsPage;
